import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loader from '../../components/helpers/Loader';
import './FeedbackDetailsPage.css';

const FeedbackDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Email functionality states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('Re: Your feedback submission');
  const [emailMessage, setEmailMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

  useEffect(() => {
    const fetchFeedbackDetails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fixed: Changed from /feedback/${id} to /get-feedback/${id} to match backend route
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/support/get-feedback/${id}`, { credentials: 'include', });
        console.log("Fetching feedback details for ID:", id);

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setFeedback({
          ...data,
          contactNo: data.phone // Map phone to contactNo for consistency
        });
        setIsLoading(false);
      } catch (err) {
        console.error("Failed to fetch feedback details:", err);
        setError(err);
        setIsLoading(false);
      }
    };

    if (id) {
      fetchFeedbackDetails();
    }
  }, [id]);

  const handleGoBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Clear email messages after timeout
  useEffect(() => {
    if (emailSuccess || emailError) {
      const timer = setTimeout(() => {
        setEmailSuccess(null);
        setEmailError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [emailSuccess, emailError]);

  const getToken = () => {
    try {
      return localStorage.getItem('token');
    } catch (error) {
      console.warn('localStorage not available:', error);
      return null;
    }
  };

  const handleReplyClick = () => {
    setShowEmailModal(true);
    setEmailSubject(`Re: Your feedback submission - ${feedback.name}`);
    setEmailMessage('Thank you for your feedback. ');
    setEmailError(null);
    setEmailSuccess(null);
  };

  const handleSendEmail = async () => {
    if (!emailMessage.trim()) {
      setEmailError('Please enter a message');
      return;
    }

    if (!feedback.email) {
      setEmailError('No email address available for this feedback');
      return;
    }

    // const token = getToken();
    // if (!token) {
    //   setEmailError('Authentication token not found');
    //   return;
    // }

    setSending(true);
    setEmailError(null);
    setEmailSuccess(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/support/feedback/${id}/reply`,
        {
          subject: emailSubject,
          message: emailMessage
        },
        {
          // headers: {
          //   Authorization: `Bearer ${token}`,
          //   'Content-Type': 'application/json',
          // },
        }
      );

      if (response.status === 200) {
        setEmailSuccess(`Email sent successfully to ${feedback.email}!`);
        setEmailMessage('');
        setEmailSubject(`Re: Your feedback submission - ${feedback.name}`);
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowEmailModal(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailError('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setEmailMessage('');
    setEmailSubject(`Re: Your feedback submission - ${feedback.name}`);
    setEmailError(null);
    setEmailSuccess(null);
  };

  if (isLoading) {
    return (
      <div className="feedback-details-container">
        <div className="loading-content">
          <Loader size={50} color="#007bff" secondaryColor="#f3f3f3" />
          <p>Loading feedback details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feedback-details-container">
        <div className="error-container">
          <h2>Error Loading Feedback</h2>
          <p>Failed to load feedback details.</p>
          <p className="error-message">Error: {error.message || 'Unknown error'}</p>
          <button className="back-button" onClick={handleGoBack}>
            ← Back to Feedback List
          </button>
        </div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="feedback-details-container">
        <div className="not-found-container">
          <h2>Feedback Not Found</h2>
          <p>The requested feedback entry could not be found.</p>
          <button className="back-button" onClick={handleGoBack}>
            ← Back to Feedback List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-details-container">
      <div className="page-header">
        <button className="back-button" onClick={handleGoBack}>
          ← Back to Feedback List
        </button>
        <h2>Feedback Details</h2>
      </div>

      <div className="feedback-details-card">
        <div className="details-header">
          <div className="user-info">
            <div className="user-avatar">
              {feedback?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <h3>{feedback.name}</h3>
              <p className="submission-date">
                Submitted on {feedback.createdAt ? new Date(feedback.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'Unknown date'}
              </p>
            </div>
          </div>
        </div>

        <div className="details-content">
          <div className="contact-section">
            <h4>Contact Information</h4>
            <div className="contact-grid">
              <div className="contact-item">
                <label>Email Address:</label>
                <div className="contact-value">
                  <a href={`mailto:${feedback.email}`} className="email-link">
                    {feedback.email}
                  </a>
                </div>
              </div>
              {feedback.contactNo && (
                <div className="contact-item">
                  <label>Phone Number:</label>
                  <div className="contact-value">
                    <a href={`tel:${feedback.contactNo}`} className="phone-link">
                      {feedback.contactNo}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="feedback-section">
            <h4>Feedback Message</h4>
            <div className="feedback-content">
              <p>{feedback.description}</p>
            </div>
          </div>

          {feedback.category && (
            <div className="category-section">
              <h4>Category</h4>
              <span className="category-tag">{feedback.category}</span>
            </div>
          )}

          {feedback.priority && (
            <div className="priority-section">
              <h4>Priority</h4>
              <span className={`priority-tag priority-${feedback.priority.toLowerCase()}`}>
                {feedback.priority}
              </span>
            </div>
          )}

          {feedback.status && (
            <div className="status-section">
              <h4>Status</h4>
              <span className={`status-tag status-${feedback.status.toLowerCase()}`}>
                {feedback.status}
              </span>
            </div>
          )}
        </div>

        <div className="details-footer">
          <div className="metadata">
            <p><strong>Feedback ID:</strong> {feedback.id}</p>
            {feedback.updatedAt && feedback.updatedAt !== feedback.createdAt && (
              <p><strong>Last Updated:</strong> {
                feedback.updatedAt ? new Date(feedback.updatedAt).toLocaleString() : 'Unknown'
              }</p>
            )}
          </div>

          <div className="action-buttons">
            <button className="reply-button" onClick={handleReplyClick}>
              📧 Reply to Feedback
            </button>
            {/* <button className="mark-resolved-button">
              Mark as Resolved
            </button> */}
          </div>
        </div>
      </div>

      {/* Email Reply Modal */}
      {showEmailModal && (
        <div className="email-modal-overlay">
          <div className="email-modal">
            <div className="email-modal-header">
              <h3>Reply to Feedback</h3>
              <button onClick={handleCloseEmailModal} className="modal-close-btn">×</button>
            </div>

            <div className="email-modal-content">
              <div className="recipient-info">
                <p><strong>To:</strong> {feedback.email}</p>
                <p><strong>Regarding:</strong> Feedback from {feedback.name}</p>
              </div>

              <div className="email-form">
                <div className="form-group">
                  <label htmlFor="email-subject">Subject:</label>
                  <input
                    id="email-subject"
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="email-subject-input"
                    placeholder="Email subject"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email-message">Message:</label>
                  <textarea
                    id="email-message"
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    className="email-message-input"
                    placeholder="Type your reply message here..."
                    rows="6"
                  />
                </div>

                {emailError && (
                  <div className="email-error-message">
                    {emailError}
                  </div>
                )}

                {emailSuccess && (
                  <div className="email-success-message">
                    {emailSuccess}
                  </div>
                )}

                <div className="email-modal-actions">
                  <button
                    onClick={handleCloseEmailModal}
                    className="cancel-email-btn"
                    disabled={sending}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sending || !emailMessage.trim()}
                    className="send-email-btn"
                  >
                    {sending ? (
                      <>
                        <Loader size={16} color="#ffffff" secondaryColor="#ffffff" />
                        Sending...
                      </>
                    ) : (
                      '📧 Send Email'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackDetails;